import {
  Card,
  CardHeader,
  FormField,
  Button,
  TextInput,
  InlineAlert,
  Select,
} from "@constructorfleet/ui/design-system";
import { useState, useEffect, useMemo } from "react";
import type { BhgCallDashboardProvider, BhgClinicOption } from "../types";

export interface BhgClinicSettingsProps {
  context?: "dashboard" | "signup";
  provider?: BhgCallDashboardProvider;
  disableInitialSync?: boolean;
  onClinicUpdated?: (clinicName: string | null) => void;
  clinics: BhgClinicOption[];
  setClinics: (clinics: BhgClinicOption[]) => void;
  currentClinicId: string;
  setCurrentClinicId: (clinicId: string) => void;
  selectedClinicId: string;
  setSelectedClinicId: (clinicId: string) => void;
  clinicName: string | null;
  setClinicName: (clinicName: string | null) => void;
  saving: boolean;
  setSaving: (saving: boolean) => void;
  message: string | null;
  setMessage: (message: string | null) => void;
}

export function BhgClinicSettings(props: BhgClinicSettingsProps) {
  const {
    context = "dashboard",
    disableInitialSync,
    onClinicUpdated,
    provider,
    clinics,
    setClinics,
    currentClinicId,
    setCurrentClinicId,
    selectedClinicId,
    setSelectedClinicId,
    clinicName,
    setClinicName,
    saving,
    setSaving,
    message,
    setMessage,
  } = props;

  useEffect(() => {
    if (disableInitialSync) {
      return;
    }
    let mounted = true;
    void (async () => {
      const [clinicList, subscription] = await Promise.all([
        provider?.fetchClinics() ?? Promise.resolve(clinics),
        provider?.fetchSubscription() ?? Promise.resolve(null),
      ]);
      if (!mounted) return;

      setClinics(clinicList);
      const enrolledClinicId = subscription?.bhgEnrollment?.clinicId;
      const idStr = enrolledClinicId ? String(enrolledClinicId) : "";
      setCurrentClinicId(idStr);
      setSelectedClinicId(idStr);
      setClinicName(subscription?.bhgEnrollment?.clinicName ?? null);
      setMessage(!idStr && context === "dashboard" ? getNoClinicMessage(context) : null);
    })();
    return () => {
      mounted = false;
    };
  }, [disableInitialSync]);

  const [clinicFilter, setClinicFilter] = useState("");
  const [stateFilter, setStateFilter] = useState("");

  const availableStates = useMemo(() => {
    const states = [...new Set(clinics.map((c) => c.state).filter(Boolean))].sort();
    return states;
  }, [clinics]);

  const filteredClinics = useMemo(() => {
    const query = clinicFilter.trim().toLowerCase();
    return clinics.filter((clinic) => {
      if (stateFilter && clinic.state !== stateFilter) return false;
      if (!query) return true;
      return (
        clinic.name.toLowerCase().includes(query) ||
        clinic.city.toLowerCase().includes(query) ||
        clinic.state.toLowerCase().includes(query)
      );
    });
  }, [clinics, clinicFilter, stateFilter]);

  const hasChanged = selectedClinicId !== currentClinicId;
  const selectedClinic = clinics.find((clinic) => String(clinic.id) === selectedClinicId) ?? null;

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    const clinicId = selectedClinicId ? Number(selectedClinicId) : null;
    const ok = (await provider?.updateClinic(clinicId)) ?? false;
    setSaving(false);
    if (ok) {
      setCurrentClinicId(selectedClinicId);
      const matched = clinics.find((c) => c.id === clinicId);
      setClinicName(matched?.name ?? null);
      onClinicUpdated?.(matched?.name ?? null);
      setMessage("Clinic updated successfully.");
    } else {
      setMessage("Failed to update clinic. Please try again.");
    }
  };

  return (
    <Card>
      <CardHeader
        title="Clinic Settings"
        meta={renderClinicSettingsDescription(context, clinicName)}
      />
      <div className="card-body ds-stack">
        <div className="clinic-settings-left-column">
          <div className="clinic-select-field">
            <div style={{ display: "grid", gap: "0.5rem" }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.5rem" }}>
                <FormField label="Filter by state" htmlFor="clinic-state-filter">
                  <Select
                    id="clinic-state-filter"
                    value={stateFilter}
                    onChange={(event) => setStateFilter(event.target.value)}
                  >
                    <option value="">All states</option>
                    {availableStates.map((state) => (
                      <option key={state} value={state}>
                        {state}
                      </option>
                    ))}
                  </Select>
                </FormField>
                <FormField label="Search clinics" htmlFor="clinic-text-filter">
                  <TextInput
                    id="clinic-text-filter"
                    type="search"
                    placeholder="Name or city…"
                    value={clinicFilter}
                    onChange={(event) => setClinicFilter(event.target.value)}
                  />
                </FormField>
              </div>
              <FormField label="Clinic location" htmlFor="dashboard-clinic-select">
                <Select
                  id="dashboard-clinic-select"
                  value={selectedClinicId}
                  onChange={(event) => {
                    setSelectedClinicId(event.target.value);
                    setMessage(null);
                  }}
                >
                  <option value="">
                    {filteredClinics.length === 0
                      ? "No clinics match your search"
                      : "Select your clinic…"}
                  </option>
                  {filteredClinics.map((clinic) => (
                    <option key={clinic.id} value={String(clinic.id)}>
                      {clinic.name} — {clinic.city}, {clinic.state}
                    </option>
                  ))}
                </Select>
              </FormField>
            </div>
          </div>
        </div>
        <div className="clinic-details-top-card">
          <ClinicDetailsPanel clinic={selectedClinic} />
        </div>
        {message ? (
          <InlineAlert tone={message.includes("Failed") ? "danger" : "success"}>
            {message}
          </InlineAlert>
        ) : null}
        {onClinicUpdated ? (
          <div className="ds-row">
            <Button
              type="button"
              disabled={!hasChanged || saving}
              onClick={() => void handleSave()}
            >
              {saving ? "Saving..." : "Update clinic"}
            </Button>
          </div>
        ) : null}
      </div>
    </Card>
  );
}

export function ClinicDetailsPanel(props: { clinic: BhgClinicOption | null }) {
  const { clinic } = props;
  if (!clinic) {
    return null;
  }

  const dosingHours = parseHoursJson(clinic.dosingHours);
  const operatingHours = parseHoursJson(clinic.operatingHours);

  return (
    <div className="ds-stack clinic-details-panel">
      <div className="clinic-details-summary">
        <div>
          <p className="clinic-details-kicker">Selected Clinic</p>
          <p className="clinic-details-name">{clinic.name}</p>
        </div>
        <div className="clinic-details-contact">
          <p>{clinic.phone}</p>
          <p>{formatClinicAddress(clinic)}</p>
        </div>
      </div>
      {(dosingHours || operatingHours) && (
        <div className="clinic-hours-row">
          {dosingHours && <ClinicHoursBlock label="Medication Hours" hours={dosingHours} />}
          {operatingHours && <ClinicHoursBlock label="Operating Hours" hours={operatingHours} />}
        </div>
      )}
    </div>
  );
}

export function ClinicHeaderDetails(props: {
  clinic: BhgClinicOption | null;
  isoDate: string | null;
}) {
  const { clinic, isoDate } = props;
  if (!clinic) {
    return null;
  }

  const weekday = getWeekdayKey(isoDate);
  const dosingHours = getHoursForDay(clinic.dosingHours, weekday);
  const operatingHours = getHoursForDay(clinic.operatingHours, weekday);

  return (
    <div className="clinic-header-details" aria-label="Selected clinic details">
      <p className="clinic-details-kicker">Selected Clinic</p>
      <div className="clinic-header-name-row">
        <p className="clinic-details-name">{clinic.name}</p>
        <p className="clinic-details-contact-line clinic-header-phone">{clinic.phone}</p>
      </div>
      <p className="clinic-details-contact-line">{formatClinicAddress(clinic)}</p>
      <div className="clinic-header-hours">
        <div>
          <p className="clinic-header-hours-label">Operating Hours</p>
          <p className="clinic-header-hours-value">{operatingHours ?? "Unavailable"}</p>
        </div>
        <div>
          <p className="clinic-header-hours-label">Medication Hours</p>
          <p className="clinic-header-hours-value">{dosingHours ?? "Unavailable"}</p>
        </div>
      </div>
    </div>
  );
}

export function ClinicHoursBlock(props: { label: string; hours: Record<string, string> }) {
  const { label, hours } = props;
  return (
    <div className="clinic-hours-block">
      <p className="clinic-hours-heading">
        <strong>{label}</strong>
      </p>
      <ul className="clinic-hours-list">
        {WEEKDAY_ORDER.map((day) => {
          const value = hours[day];
          if (!value) return null;
          return (
            <li key={day}>
              <span className="clinic-hours-day">
                {day.charAt(0).toUpperCase() + day.slice(1, 3)}
              </span>
              <span className="clinic-hours-time">{value}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
export const WEEKDAY_ORDER = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
] as const;

export function parseHoursJson(value: string | null): Record<string, string> | null {
  if (!value) {
    return null;
  }

  try {
    const parsed = JSON.parse(value) as Record<string, unknown>;
    const entries = Object.entries(parsed).filter(
      (entry): entry is [string, string] => typeof entry[1] === "string"
    );
    return entries.length > 0 ? Object.fromEntries(entries) : null;
  } catch {
    return null;
  }
}

export function formatClinicAddress(
  clinic: Pick<BhgClinicOption, "streetAddress" | "city" | "state" | "postalCode">
) {
  return [clinic.streetAddress, `${clinic.city}, ${clinic.state} ${clinic.postalCode}`]
    .filter((part) => part.trim().length > 0)
    .join(", ");
}

export function getHoursForDay(value: string | null, day: string | null) {
  if (!day) {
    return null;
  }
  const hours = parseHoursJson(value);
  return hours?.[day] ?? null;
}

export function getWeekdayKey(isoDate: string | null) {
  if (!isoDate) {
    return null;
  }
  const parsed = new Date(`${isoDate}T12:00:00.000Z`);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }
  return WEEKDAY_ORDER[(parsed.getUTCDay() + 6) % 7] ?? null;
}

function renderClinicSettingsDescription(
  context: "dashboard" | "signup",
  clinicName: string | null
) {
  if (context === "signup") {
    return "Select the clinic where your daily calls should be placed.";
  }

  return (
    <>
      Your calls are placed to your selected clinic.
      {clinicName ? (
        <>
          {" "}
          Current clinic: <strong>{clinicName}</strong>
        </>
      ) : (
        <> No clinic selected.</>
      )}
    </>
  );
}

function getNoClinicMessage(context: "dashboard" | "signup") {
  if (context === "signup") {
    return null;
  }

  return "No clinic selected. Please select your clinic from the dropdown below.";
}
